/**
 * <ai-product-detail> — 产品信息联动容器组件（PDP 场景）
 *
 * 职责：
 * - 监听子元素 <ai-variant-selector> 的 variant:change 事件
 * - 自动更新产品图片（[data-product-featured-image]）
 * - 自动更新价格（[data-product-price]）、原价（[data-product-compare-price]）
 * - 自动更新折扣标签（[data-product-badge]）
 * - 不负责任何样式，样式由 LLM 在 section CSS 中定义
 *
 * 用法：
 * <ai-product-detail>
 *   <script type="application/json" data-product-variants>{{ product.variants | json }}</script>
 *   <script type="application/json" data-product-options>{{ product.options_with_values | json }}</script>
 *   <img data-product-featured-image src="..." />
 *   <span data-product-price>{{ product.price | money }}</span>
 *   <s data-product-compare-price>{{ product.compare_at_price | money }}</s>
 *   <ai-variant-selector selected-variant-id="..." />
 *   <ai-quick-add-cart product-id="..." variant-id="..." />
 * </ai-product-detail>
 *
 * data 属性约定：
 * - data-product-featured-image : img 元素，切换变体时更新 src
 * - data-product-price          : 当前价格，切换变体时更新文本
 * - data-product-compare-price  : 原价/划线价，无原价时 hidden
 * - data-product-badge          : 折扣标签（如 "Save 20%"），无折扣时 hidden
 *
 * 组件透明：不创建额外 DOM，不使用 Shadow DOM，AI 可以自由设计内部 HTML 和样式。
 */

if (!customElements.get("ai-product-detail")) {
    customElements.define(
        "ai-product-detail",
        class ProductInfo extends HTMLElement {
            // ══════════════════════════════════════════
            //  生命周期
            // ══════════════════════════════════════════

            connectedCallback() {
                // 监听子元素 variant:change（variant 数据从事件 detail 获取）
                this.addEventListener("variant:change", this._onVariantChange.bind(this));
            }

            disconnectedCallback() {
                this.removeEventListener("variant:change", this._onVariantChange);
            }

            // ══════════════════════════════════════════
            //  variant:change 处理
            // ══════════════════════════════════════════

            _onVariantChange(e) {
                const { variant } = e.detail;
                if (!variant) return;

                this._updateImage(variant);
                this._updatePrice(variant);
                this._updateBadge(variant);
            }

            // ══════════════════════════════════════════
            //  图片更新
            // ══════════════════════════════════════════

            _updateImage(variant) {
                const imgEl = this.querySelector("[data-product-featured-image]");
                if (!imgEl) return;

                const newSrc = this._getVariantImageSrc(variant);
                if (newSrc && imgEl.src !== newSrc.src) {
                    imgEl.src = newSrc.src;
                }
            }

            /**
             * 从 variant 中提取图片 URL
             * 兼容多种数据结构：featured_image / featured_media / image
             */
            _getVariantImageSrc(variant) {
                if (!variant) return null;

                // featured_image
                const fi = variant.featured_image;
                if (fi) {
                    if (typeof fi === "string") return fi;
                    if (fi.src) return fi.src;
                    if (fi.url) return fi.url;
                }

                // featured_media
                const fm = variant.featured_media;
                if (fm) {
                    const preview = fm.preview_image;
                    if (preview) {
                        if (typeof preview === "string") return preview;
                        if (preview.src) return preview.src;
                    }
                    if (fm.src) return fm.src;
                }

                // image
                const img = variant.image;
                if (img) {
                    if (typeof img === "string") return img;
                    if (img.src) return img.src;
                    if (img.url) return img.url;
                }

                return variant.featured_image_url || variant.image_url || null;
            }

            // ══════════════════════════════════════════
            //  价格更新
            // ══════════════════════════════════════════

            _updatePrice(variant) {
                const priceEl = this.querySelector("[data-product-price]");
                const compareEl = this.querySelector("[data-product-compare-price]");

                if (priceEl) {
                    priceEl.textContent = this._replaceMoneyAmount(
                        priceEl.textContent.trim(),
                        variant.price,
                    );
                }

                const hasCompare =
                    variant.compare_at_price != null &&
                    variant.compare_at_price > variant.price;

                if (compareEl) {
                    if (hasCompare) {
                        compareEl.textContent = this._replaceMoneyAmount(
                            compareEl.textContent.trim() || priceEl?.textContent?.trim() || "",
                            variant.compare_at_price,
                        );
                        compareEl.hidden = false;
                    } else {
                        compareEl.hidden = true;
                    }
                }
            }

            // ══════════════════════════════════════════
            //  折扣标签更新
            // ══════════════════════════════════════════

            _updateBadge(variant) {
                const badgeEl = this.querySelector("[data-product-badge]");
                if (!badgeEl) return;

                const hasDiscount =
                    variant.compare_at_price != null &&
                    variant.compare_at_price > variant.price;

                if (!hasDiscount) {
                    badgeEl.hidden = true;
                    return;
                }

                const discount = variant.compare_at_price - variant.price;
                const percent = Math.ceil(
                    (discount / variant.compare_at_price) * 100,
                );

                // 根据 data-badge-format 属性选择格式
                const format = badgeEl.getAttribute("data-badge-format") || "percentage";
                badgeEl.textContent =
                    format === "percentage" ? `-${percent}%` : this._replaceMoneyAmount(
                        badgeEl.textContent.trim() || this.querySelector("[data-product-price]")?.textContent?.trim() || "",
                        discount,
                    );
                badgeEl.hidden = false;
            }

            // ══════════════════════════════════════════
            //  货币格式化（保留原始货币符号）
            //  参考 variants-edit-cart.js replaceMoneyAmount
            // ══════════════════════════════════════════

            /**
             * 将已格式化的价格字符串中的数字部分替换为新值，
             * 保留原始的货币符号、位置和分隔符格式。
             *
             * 支持：
             * - 符号在前: $100.00 / € 100,00
             * - 符号在后: 100.00€ / 100,00 ر.س (RTL)
             *
             * @param {string} input - 当前显示的价格文本（如 "$99.00"）
             * @param {number} newValue - 新的价格数值（单位：分/最小货币单位，如 9900）
             * @returns {string} 替换后的价格文本
             */
            _replaceMoneyAmount(input, newValue) {
                if (!input || newValue == null) {
                    return input || "";
                }

                // 将分转成元（Shopify 价格单位是分）
                const amount = Number(newValue) / 100;

                // 匹配两种格式：
                // 1. 符号在前: $100.00 / € 100,00
                // 2. 符号在后: 100.00€ / 100,00 ر.س (RTL)
                const symbolFirstPattern = /^([^\d\s]+)\s?([\d,]*\.?\d+)(.*)$/;
                const numberFirstPattern = /^([\d,]*\.?\d+)\s?([^\d\s].*)$/;

                const symbolFirstMatch = input.match(symbolFirstPattern);
                const numberFirstMatch = input.match(numberFirstPattern);

                let oldNumber, isSymbolFirst;

                if (symbolFirstMatch && symbolFirstMatch[2]) {
                    oldNumber = symbolFirstMatch[2];
                    isSymbolFirst = true;
                } else if (numberFirstMatch && numberFirstMatch[1]) {
                    oldNumber = numberFirstMatch[1];
                    isSymbolFirst = false;
                } else {
                    // 都不匹配，直接返回格式化后的数字
                    return amount.toFixed(2);
                }

                // 检查原数字部分是否带有逗号 → 决定是否加千分位
                const useThousandSeparator = oldNumber.includes(",");
                const hasDecimal = /\.\d+$/.test(oldNumber);
                const decimalPlaces = hasDecimal ? oldNumber.split(".")[1].length : 0;

                const formatted = useThousandSeparator
                    ? amount.toLocaleString("en-US", {
                        minimumFractionDigits: decimalPlaces,
                        maximumFractionDigits: decimalPlaces,
                    })
                    : amount.toFixed(decimalPlaces);

                if (isSymbolFirst) {
                    const [fullMatch, symbol, , suffix] = symbolFirstMatch;
                    const hasSpaceAfterSymbol =
                        fullMatch.charAt(symbol.length) === " ";
                    return (
                        symbol + (hasSpaceAfterSymbol ? " " : "") + formatted + suffix
                    );
                } else {
                    const [fullMatch, number, suffix] = numberFirstMatch;
                    const hasSpaceBeforeSuffix =
                        fullMatch.charAt(number.length) === " ";
                    return (
                        formatted +
                        (hasSpaceBeforeSuffix ? " " : "") +
                        suffix.trim()
                    );
                }
            }

        },
    );
}
